import { Grid, Typography, styled } from "@mui/material";
import { useTheme } from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxDeleteDialog from "components/UI/AcxDeleteDialog";
import ErrorDialog from "components/UI/AcxErrorDialog";
import AcxImageFileDrop from "components/UI/AcxImageFileDrop";
import AcxImageTile from "components/UI/AcxImageTile";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import AcxMainTextField from "components/UI/AcxMainTextField";
import VerticalDrawerContentTemplate from "components/UI/Drawer/VerticalDrawerContentTemplate";
import { observer } from "mobx-react";
import {
    CSSProperties,
    forwardRef,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import FilesService from "services/FilesService";
import { useStore } from "utils/useStore";
import { SavedFilter } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { SignalsService } from "services/SignalsService";
import DashboardStore from "./DashboardStore";

interface ContainerProps {
    style?: CSSProperties;
    children?: React.ReactNode;
}

const Container = forwardRef<HTMLDivElement, ContainerProps>((props, ref) => {
    const theme = useTheme();

    const containerStyle: CSSProperties = {
        backgroundColor: theme.palette.gray[50],
        height: "375px",
        width: "100%",
        display: "grid",
        flexDirection: "column",
        padding: "10px",
        overflowY: "scroll",
        overflowX: "hidden",
        borderRadius: "4px",
        border: `1px solid ${theme.palette.gray[300]}`,
        gridTemplateColumns: "repeat(2, minmax(0, 226px))",
        gridAutoRows: "163px",
        columnGap: "8px",
        rowGap: "8px",
        ...props.style,
    };

    return (
        <div ref={ref} style={containerStyle}>
            {props.children}
        </div>
    );
});
Container.displayName = "Container";

// Keep in sync with the backend's DashboardDescriptionMaxLength ceiling (1,000);
// the UI intentionally caps lower so the header stays readable.
const DESCRIPTION_MAX_LENGTH = 250;

type Prop = {
    dashboardId: string;
    currentName: string;
    currentPinnedFilter?: SavedFilter;
    dashboard?: DashboardStore;
    updateDashboard: ({
        title,
        description,
        pinnedApplicationFilterId,
        logoName,
    }: {
        title: string;
        description?: string;
        pinnedApplicationFilterId: string | undefined;
        logoName: string | undefined;
    }) => Promise<void>;
    closeDrawer: () => void;
};

const CustomizeDashboard: React.FC<Prop> = observer((props: Prop) => {
    const theme = useTheme();
    const messageStore = useStore(MessageStore);

    // Values Held By Customize Dashboard Separate From Current Dashboard
    const [customizedName, setCustomizedName] = useState<string>(
        props.currentName ?? "",
    );

    const [customizedDescription, setCustomizedDescription] = useState<string>(
        props.dashboard?.description ?? "",
    );

    const [customizedLogoName, setCustomizedLogoName] = useState<
        string | undefined
    >(props.dashboard?.logoName);
    const [editsMade, setEditsMade] = useState(false);

    useEffect(() => {
        // Whenever our passed in name (the current dashboard name) changes, we can reset our local state
        setCustomizedName(props.currentName);
    }, [props.currentName]);

    useEffect(() => {
        setCustomizedDescription(props.dashboard?.description ?? "");
    }, [props.dashboard?.description]);

    // Maintain Editing Status
    useEffect(() => {
        const originalName = props.currentName;
        const originalDescription = props.dashboard?.description ?? "";
        const originalLogoName = props.dashboard?.logoName;

        if (
            customizedName !== originalName ||
            customizedDescription !== originalDescription ||
            customizedLogoName !== originalLogoName
        ) {
            setEditsMade(true);
        } else {
            setEditsMade(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customizedName, customizedDescription, customizedLogoName]);

    const [loading, setLoading] = useState(true);
    const [orgLogos, setOrgLogos] = useState<
        { blobSasUrl: string; fileName: string }[]
    >([]);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [errorDetails, setErrorDetails] = useState<{
        errorTitle: string;
        errorMessage: string;
    }>({ errorTitle: "", errorMessage: "" });
    const [stagedDeleteLogo, setStagedDeleteLogo] = useState<null | string>(
        null,
    );
    const [deleteLogoLoading, setDeleteLogoLoading] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const scrollPositionRef = useRef(0);
    const logoObjectUrlsRef = useRef<string[]>([]);

    const signalsService = useRef<SignalsService>(new SignalsService()).current;
    const filesService = useRef<FilesService>(new FilesService()).current;

    function revokeLogoObjectUrls() {
        logoObjectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        logoObjectUrlsRef.current = [];
    }

    async function getLogos() {
        const logos: { fileName: string }[] = await signalsService.getLogos();
        const logoResults = await Promise.allSettled(
            logos.map(async ({ fileName }) => {
                const logoBlob = await filesService.downloadLogoFile(fileName);
                const blobSasUrl = URL.createObjectURL(logoBlob);
                return { blobSasUrl, fileName };
            }),
        );

        const nextOrgLogos = logoResults.flatMap((result) =>
            result.status === "fulfilled" ? [result.value] : [],
        );

        revokeLogoObjectUrls();
        logoObjectUrlsRef.current = nextOrgLogos.map((logo) => logo.blobSasUrl);
        setOrgLogos(nextOrgLogos);
        setLoading(false);
    }

    useEffect(() => {
        void getLogos();

        return () => {
            revokeLogoObjectUrls();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const SingleDropContainer = styled("div")({
        height: "150px",
        width: "100%",
        display: "grid",
        padding: "10px",
        gridTemplateColumns: "1fr",
        gridAutoRows: "163px",
    });

    // Ensure Scroll Position is maintained on renders
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const container = containerRef.current;

        const handleScroll = () => {
            if (container) {
                scrollPositionRef.current = container.scrollTop;
            }
        };

        if (container) {
            container.addEventListener("scroll", handleScroll);
        }

        return () => {
            if (container) {
                container.removeEventListener("scroll", handleScroll);
                if (containerRef.current) {
                    containerRef.current.scrollTop = scrollPositionRef.current;
                }
            }
        };
    }, [orgLogos, loading, customizedLogoName]);

    async function saveLogo(file: File) {
        await signalsService.saveLogo(file, props.dashboardId);
        await getLogos();
        selectLogo(file.name);
    }

    const selectLogo = useCallback((logoClicked: string) => {
        setEditsMade(true);
        setCustomizedLogoName((previousLogoValue) => {
            if (!previousLogoValue || previousLogoValue !== logoClicked) {
                return logoClicked;
            } else {
                return undefined;
            }
        });
    }, []);

    const deleteLogo = useCallback(
        async (logoClicked: string) => {
            const deleteCurrentLogo = logoClicked === customizedLogoName;
            setDeleteLogoLoading(true);

            // If the logo to be deleted is the current logo for the dashboard,
            // we need to remove the logo as the currently selected logo
            if (deleteCurrentLogo) {
                setCustomizedLogoName(undefined);
            }

            try {
                await signalsService.deleteLogo(logoClicked, props.dashboardId);
            } catch (e) {
                messageStore.logError("Error deleting logo");
            } finally {
                setStagedDeleteLogo(null);
                await getLogos();
                props.updateDashboard({
                    title: customizedName,
                    description: customizedDescription.trim(),
                    pinnedApplicationFilterId:
                        props.dashboard?.pinnedApplicationFilterId,
                    logoName: deleteCurrentLogo
                        ? undefined
                        : customizedLogoName,
                });
                setEditsMade(false);
                setDeleteLogoLoading(false);
            }
        },
        // customizedDescription is included so the flush on logo delete persists the
        // draft description instead of the value captured when the drawer opened.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.dashboardId, customizedDescription],
    );

    const handleFileUploadError = (
        errorTitle: string,
        errorMessage: string,
    ) => {
        setErrorDetails({ errorTitle, errorMessage });
        setErrorDialogOpen(true);
    };

    function getContent() {
        if (containerRef.current != null) {
            containerRef.current.scrollTop = scrollPositionRef.current;
        }

        if (loading) {
            return <AcxLoadingIndicator size={75} alternate="PuffLoader" />;
        }

        return (
            <div
                style={{
                    // height: "calc(100% - 2rem)",
                    height: "100%",
                    paddingLeft: "8px",
                    paddingRight: "8px",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <div style={{ marginTop: "24px" }}>
                    <AcxMainTextField
                        id="customize-dashboard-name"
                        labelText="Dashboard Name"
                        value={customizedName}
                        fullWidth
                        onChange={(e) => setCustomizedName(e.target.value)}
                    />
                </div>

                <div style={{ marginTop: "24px" }}>
                    <AcxMainTextField
                        id="customize-dashboard-description"
                        labelText="Dashboard Description"
                        labelEndText={`${customizedDescription.length}/${DESCRIPTION_MAX_LENGTH}`}
                        value={customizedDescription}
                        placeholderText="Add a description of this dashboard here."
                        fullWidth
                        multiline
                        multilinePadding
                        rowsMax={3}
                        maxLength={DESCRIPTION_MAX_LENGTH}
                        onChange={(e) =>
                            setCustomizedDescription(
                                e.target.value.slice(0, DESCRIPTION_MAX_LENGTH),
                            )
                        }
                    />
                </div>

                <div style={{ marginTop: "24px" }}>
                    <Typography
                        style={{
                            color: theme.palette.neutral.main,
                            fontFamily: "Inter",
                            fontStyle: "normal",
                            fontSize: "16px",
                            fontWeight: 600,
                            lineHeight: "24px",
                        }}
                    >
                        {"Add Logo"}
                    </Typography>
                    <Typography
                        style={{
                            color: "#3F3F46",
                            fontFamily: "Inter",
                            fontStyle: "normal",
                            fontSize: "12px",
                            fontWeight: 400,
                            lineHeight: "16px",
                            marginBottom: "24px",
                        }}
                    >
                        {"Personalize your dashboard by adding a company logo."}
                    </Typography>
                    {!!orgLogos.length ? (
                        <Container ref={containerRef}>
                            <AcxImageFileDrop
                                uploadHandler={saveLogo}
                                onError={handleFileUploadError}
                            />
                            {orgLogos.map((orgLogo) => {
                                const isHighlighted =
                                    customizedLogoName === orgLogo.fileName;

                                return (
                                    <AcxImageTile
                                        key={orgLogo.fileName}
                                        imageUri={orgLogo.blobSasUrl}
                                        imageName={orgLogo.fileName}
                                        highlighted={isHighlighted}
                                        onClickHandler={selectLogo}
                                        onDeleteHandler={(logo) => {
                                            setStagedDeleteLogo(logo);
                                        }}
                                    />
                                );
                            })}
                        </Container>
                    ) : (
                        <SingleDropContainer>
                            <AcxImageFileDrop
                                uploadHandler={saveLogo}
                                onError={handleFileUploadError}
                            />
                        </SingleDropContainer>
                    )}
                </div>

                <ErrorDialog
                    isOpen={errorDialogOpen}
                    onConfirm={() => setErrorDialogOpen(false)}
                    onClose={() => setErrorDialogOpen(false)}
                    title={errorDetails.errorTitle}
                    subTitle={errorDetails.errorMessage}
                />
                <AcxDeleteDialog
                    isOpen={!!stagedDeleteLogo}
                    onCancel={() => {
                        setStagedDeleteLogo(null);
                    }}
                    onDelete={() => {
                        if (stagedDeleteLogo) {
                            deleteLogo(stagedDeleteLogo);
                        }
                    }}
                    text={{
                        title: "Are you sure?",
                        content:
                            "Deleting this image is immediate and permanent. Are you sure you want to delete?",
                        confirm: "Delete Image",
                    }}
                    loading={deleteLogoLoading}
                />
            </div>
        );
    }

    return (
        <Grid container item xs={12} style={{ height: "100%" }}>
            <VerticalDrawerContentTemplate
                title={"Edit Dashboard"}
                titleStyles={{}}
                titleTextStyle={{
                    marginTop: "16px",
                    paddingLeft: "16px",
                    color: theme.palette.neutral.main,
                    fontSize: "20px",
                    lineHeight: "32px",
                    fontWeight: 600,
                }}
                subTitle={"Edit this dashboard's settings"}
                subTitleStyles={{}}
                subtitleTextStyle={{
                    marginBottom: "16px",
                    paddingLeft: "16px",
                    color: theme.palette.gray.main,
                    fontSize: "14px",
                    lineHeight: "20px",
                    fontWeight: 400,
                }}
                contentStyles={{ flexGrow: 1 }}
                content={getContent()}
                onClose={() => {
                    setCustomizedLogoName(props.dashboard?.logoName);
                    setCustomizedDescription(props.dashboard?.description ?? "");
                }}
                footerButtons={
                    <Grid
                        container
                        item
                        sx={{
                            justifyContent: "flex-end",
                            marginTop: "auto",
                            marginBottom: "24px",
                        }}
                        columnSpacing={1}
                    >
                        <Grid item>
                            <AcxButton
                                color="white"
                                variant="outlined"
                                onClick={() => {
                                    setEditsMade(false);
                                    setCustomizedLogoName(props.dashboard?.logoName);
                                    setCustomizedDescription(
                                        props.dashboard?.description ?? "",
                                    );
                                    props.closeDrawer();
                                }}
                            >
                                Cancel
                            </AcxButton>
                        </Grid>
                        <Grid item>
                            <AcxButton
                                color="primary"
                                disabled={!editsMade}
                                onClick={() => {
                                    props.updateDashboard({
                                        title: customizedName,
                                        description:
                                            customizedDescription.trim(),
                                        pinnedApplicationFilterId:
                                            props.dashboard
                                                ?.pinnedApplicationFilterId,
                                        logoName: customizedLogoName,
                                    });
                                    setEditsMade(false);
                                    props.closeDrawer();
                                }}
                            >
                                Update Dashboard
                            </AcxButton>
                        </Grid>
                    </Grid>
                }
            />
        </Grid>
    );
});

export default CustomizeDashboard;

